import { InputManager } from '../../game/Input';
import { TouchInput, isTouchDevice } from '../../game/TouchInput';
import { StickInput } from '../../game/StickInput';
import { controlMode, applyControlMode } from '../../ui/ControlMode';
import { keyboardDriveMode } from '../../ui/KeyboardDriveMode';
import type { System } from '../ServiceLocator';

export interface InputCallbacks {
  onAnyStart: () => void;
  onReset: () => void;
  onPauseToggle: () => void;
  onCameraToggle: () => void;
}

export class InputSystem implements System {
  readonly name = 'InputSystem';

  input!: InputManager;
  readonly touchActive: boolean;

  private touchInput: TouchInput | null = null;
  private stickInput: StickInput | null = null;
  private readonly unsubscribers: Array<() => void> = [];

  constructor(
    private readonly params: URLSearchParams,
    private readonly callbacks: InputCallbacks,
  ) {
    this.touchActive = isTouchDevice() || this.params.has('touch');
  }

  init(): void {
    this.input = new InputManager(this.callbacks);
    this.input.attach();
    this.unsubscribers.push(keyboardDriveMode.subscribe(() => this.input.reset()));

    if (this.touchActive) {
      document.body.classList.add('is-touch');
      applyControlMode(controlMode.get());
      this.applyTouchSourceForMode(controlMode.get());
      this.unsubscribers.push(controlMode.subscribe((next) => this.applyTouchSourceForMode(next)));
    }
  }

  tickFixed(): void {
    this.stickInput?.update();
  }

  reset(): void {
    this.input.reset();
  }

  resetTouch(): void {
    this.touchInput?.reset();
  }

  /** Fields this system contributes to the window.__twin debug surface. */
  debugEntries(): Record<string, unknown> {
    return { input: this.input };
  }

  dispose(): void {
    for (const unsubscribe of this.unsubscribers.splice(0)) unsubscribe();
    this.input?.detach();
    this.touchInput?.detach();
    this.touchInput = null;
    this.stickInput?.detach();
    this.stickInput = null;
  }

  private applyTouchSourceForMode(next: 'buttons' | 'sticks'): void {
    if (next === 'sticks') {
      if (this.touchInput) { this.touchInput.detach(); this.touchInput = null; }
      if (!this.stickInput) {
        this.stickInput = new StickInput();
        this.stickInput.attach();
      }
      this.input.setTouch(this.stickInput);
    } else {
      if (this.stickInput) { this.stickInput.detach(); this.stickInput = null; }
      if (!this.touchInput) {
        this.touchInput = new TouchInput();
        this.touchInput.attach();
      }
      this.input.setTouch(this.touchInput);
    }
  }
}
